/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import MainPage from '../../MainPage';
import { Divider, Input, Spin, Upload } from 'antd';
import {
  CloseIcon,
  ContactImage,
  DeleteIcon,
  EditIcon,
  NewCloseIcon,
  NewUpdateIcon,
  ToggleIcon,
} from '../Common/Icons';
import {
  Actions,
  PhotoResolution,
  RoleType,
  UserRoles,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import useUserInfo from '../Hooks/useUserInfo';
import Roles from '../Roles/Roles';
import { useCallback } from 'react';
import RebootResetModal from '../Devices/RebootResetModal';
import {
  getProfilePicUrl,
  getBase64,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import NewCustomButton from '../Common/NewCustomButton';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import CustomDialogBox from '../Common/CustomDailogBox';
import { ButtonTypes } from '../Common/CustomButton';
import { useQueryClient } from '@tanstack/react-query';
import { emailPattern } from '@/utilities/helpers/commonHelper';

const RequiredFields = {
  firstname: 'First Name',
  lastname: 'Last Name',
  email: 'Email',
};

const DefaultValues = {
  firstname: '',
  lastname: '',
  email: '',
  staffrole: '',
  staffadmin: false,
};

const formatData = (data, isEditForm, role, profilePhoto) => {
  const userObj = { rolename: UserRoles.staff };
  Object.keys(DefaultValues).forEach((key) => (userObj[key] = data[key]));
  if (isEditForm) {
    delete userObj.email;
    userObj.useruuid = data.useruuid;
  }
  userObj.staffadmin = role !== UserRoles.staff;
  if (profilePhoto) {
    userObj.profileimage = profilePhoto;
  }
  return userObj;
};

const StaffForm = (props) => {
  const {
    parentBread,
    record,
    action,
    onActionClick,
    staffList,
    isModal = false,
    currentUser,
    handleModalClose,
  } = props;
  const [role, setRole] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errorData, setErrorData] = useState('');
  // Basic UI states
  const [areButtonsDisabled, setAreButtonsDisabled] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const queryClient = useQueryClient();
  const trans = useSelector(translator);
  const userApi = useSelector((state) => state.apis.userApi);

  const currentRole = useMemo(
    () => (action === Actions.editStaff ? record?.permission_level : null),
    [action, record]
  );
  const isEditForm = action === Actions.editStaff;

  const { data: userInfo = {}, isFetching: fetchingUserInfo } = useUserInfo({
    useruuid: isEditForm && record?.useruuid,
  });
  const [isValid, setIsValid] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState(record ?? DefaultValues);

  const [emailError, setEmailError] = useState('');
  const [nameFieldError, setNameFieldError] = useState('');

  // Profile photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotourl, setProfilePhotoUrl] = useState('');
  const [isProfilePhotoUpdated, setIsProfilePhotoUpdated] = useState(false);

  // For images
  const resourcesMap = useSelector(getProfilePics());
  const photoUrl = getProfilePicUrl(
    isEditForm && record?.useruuid,
    resourcesMap,
    PhotoResolution.R256
  );

  // For Delete Staff dailog
  const [isDialogModalOpen, setDialogIsModalOpen] = useState(false);

  const handleDialogClick = () => {
    setDialogIsModalOpen(true);
  };
  const handleModalConfirm = () => {
    setDialogIsModalOpen(false);
    deleteStaff();
  };
  const handleModalCancel = () => {
    setDialogIsModalOpen(false);
  };
  // staff deletion api call
  const deleteStaff = () => {
    setProcessing(false);
    const errorCallback = (error) => {
      setProcessing(false);
      console.error('Error deleting Staff:', error);
    };
    const successCallback = (data) => {
      setProcessing(false);
      onActionClick(null, null, true)();
      queryClient.invalidateQueries({ queryKey: ['adminDashboardByDateTime'] });
    };
    try {
      setProcessing(true);
      userApi?.removeUserById(
        record?.useruuid,
        apiCallback({
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };
  // permission for delete staff
  const allowDeletePermission = useMemo(() => {
    if (currentUser?.permission_level === 'Staff Admin') {
      return true;
    } else {
      return false;
    }
  }, [currentUser]);

  const isSameUser = useMemo(() => {
    return currentUser?.username === record?.email;
  }, [currentUser, record]);

  // validating changes in form for editing
  useEffect(() => {
    if (!isEditForm || !userInfo) return;

    const hasFormChanged =
      formData.firstname !== userInfo.firstname ||
      formData.lastname !== userInfo.lastname ||
      formData.email !== userInfo.email ||
      formData.staffrole !== userInfo.staffrole ||
      role !== currentRole ||
      isProfilePhotoUpdated;

    setAreButtonsDisabled(!hasFormChanged);
  }, [formData, role, isProfilePhotoUpdated, userInfo, currentRole]);

  useEffect(() => {
    if (isEditForm && photoUrl) {
      setProfilePhotoUrl(photoUrl);
    }
  }, [isEditForm, photoUrl]);

  // Cancels the submit error modal
  const cancelSubmitError = () => {
    setShowModal(false);
  };
  // Duplication dailog for staff
  const modalDataForStaffError = {
    title: <div className="title ">{trans('DUPLICATEEMAIL')}</div>,
    description: (
      <div className="description" style={{ fontWeight: '400' }}>
        {trans('THISEMAILIS')}{' '}
        <span style={{ fontWeight: '700' }}>{trans('ALREADY_L_ASSIGNED_L')}</span>{' '}
        {trans('TOANOTHERUSER_L_TRYWITHANOTHER')}
      </div>
    ),
    action: 'staff-form',
    returnBack: cancelSubmitError,
  };

  // Form data validation
  const validate = (showError = true) => {
    const values = formData;
    let valid = true;
    let erroredFields = [];
    for (let field of Object.keys(RequiredFields)) {
      if (!values.hasOwnProperty(field) || !values[field]) {
        erroredFields.push(RequiredFields[field]);
        valid = false;
      }
    }

    if (!role) {
      erroredFields.push('Role');
      valid = false;
    }

    if (showError && !valid) {
      let error = trans('PLEASEFILLREQUIREDFIELDS') + erroredFields.join(', ');
      // message.error(error, 10);
      console.error(error, 10);
    }
    setIsValid(valid && !emailError);
    return valid && !emailError;
  };
  // Handles changes in the input fields
  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    if (name === 'firstname' || name === 'lastname') {
      setNameFieldError('');
    }
    if (name === 'email') {
      const isValidEmail = emailPattern.test(value);
      setEmailError(isValidEmail ? '' : trans('PLEASEENTERAVALIDEMAILADDRESS'));
    }
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    validate(false); // Don't show error messages while typing
  }, [formData, role, emailError]);

  // Handles the form submission
  const handleSubmit = () => {
    const errorCallback = (error) => {
      setProcessing(false);
      const msg = `Error ${isEditForm ? 'updating' : 'adding'} staff`;
      console.error(msg, error);
      setNameFieldError(error?.info || msg);
      isEditForm && (setShowModal(true), setErrorData(modalDataForStaffError));
    };
    const successCallback = (data) => {
      setProcessing(false);
      const msg = `Staff ${isEditForm ? 'updated' : 'added'} successfully!`;
      onActionClick(null, isEditForm ? record : null, true)();
      queryClient.invalidateQueries({ queryKey: ['adminDashboardByDateTime'] });
    };
    try {
      const valid = validate();
      if (!valid) return;
      const formattedData = formatData(formData, isEditForm, role, null); // exclude profilePhoto here

      if (isEditForm) {
        const hasFormChanged = JSON.stringify(formData) !== JSON.stringify(userInfo);
        if (isProfilePhotoUpdated && !hasFormChanged) {
          // Only profile photo changed
          onProfilePhotoUpdate(profilePhoto);
          return;
        }
        // If form changed or both form and photo changed
        setProcessing(true);
        userApi.updateStaffUser(
          formattedData,
          apiCallback({
            translator: trans,
            successCallback: () => {
              if (isProfilePhotoUpdated) {
                onProfilePhotoUpdate(profilePhoto);
              } else {
                successCallback();
              }
            },
            errorCallback,
            failCallback: errorCallback,
          })
        );
      } else {
        // Add staff (ignore profile photo)
        setProcessing(true);
        userApi.addStaffUser(
          formattedData,
          apiCallback({
            translator: trans,
            successCallback,
            errorCallback,
            failCallback: errorCallback,
          })
        );
      }
    } catch (error) {
      errorCallback(error);
    }
  };

  // For updating the user’s profile photo in a separate call
  const onProfilePhotoUpdate = (photo) => {
    const errorCallback = (error) => {
      setIsUpdatingProfile(false);
      setIsProfilePhotoUpdated(false);
      console.error('Error uploading picture:', error);
      // message.error("Error uploading picture");
      onActionClick(null, null, true)();
    };
    const successCallback = (data) => {
      setIsUpdatingProfile(false);
      setAreButtonsDisabled(true);
      setIsProfilePhotoUpdated(false);
      //message.success("Profile picture updated successfully");
      onActionClick(null, null, true)();
    };
    try {
      const payload = photo ? { profileimage: photo } : { profileimage: '' };
      userApi.upload(
        record?.useruuid,
        payload,
        apiCallback({
          translator: trans,
          failCallback: errorCallback,
          errorCallback,
          successCallback,
        })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  // Handles the selection of the staff member's role
  const handleRoleSelection = useCallback((selectedRole) => {
    setRole(selectedRole);
  }, []);

  // Renders the form input fields
  const renderFormItem = ({ name, label, disabled, placeholder, className, tabIndex }) => (
    <div className={`form-item form-item-verticle ${className ?? ''}`}>
      <label>{label}</label>
      <Input
        name={name}
        placeholder={placeholder}
        value={formData[name]}
        onChange={handleInputChange}
        disabled={disabled}
        tabIndex={tabIndex} //handling tab orders through index
      />
      <div style={{ height: '0.5rem', marginTop: '0.3rem', color: 'red' }}>
        {name === 'email' && emailError && <span>{emailError}</span>}
      </div>
    </div>
  );

  // Check if this staff is the only Staff Admin
  const isLastStaffAdmin = (record) => {
    return (
      record?.permission_level === 'Staff Admin' &&
      staffList.filter((staff) => staff.permission_level === 'Staff Admin').length === 1
    );
  };
  const renderRole = ({ label, className }) => {
    const allowRoleChange = isEditForm && isLastStaffAdmin(record);
    return (
      <div className={`form-item-verticle ${className ?? ''}`}>
        <label>{label}</label>
        <Roles
          contact={true}
          type={RoleType.staff}
          onHandleSelection={handleRoleSelection}
          currentRole={currentRole}
          allowRoleChange={allowRoleChange}
          staffAction={true}
        />
      </div>
    );
  };
  // Handling file upload for the avatar
  const handleUploadChange = (info) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done') {
      const file = info.file.originFileObj;
      if (file) {
        if (profilePhotourl && profilePhotourl.startsWith('blob:')) {
          URL.revokeObjectURL(profilePhotourl);
        }
        const blobUrl = URL.createObjectURL(file);
        setIsProfilePhotoUpdated(true);
        setAreButtonsDisabled(false);
        setProfilePhoto(file);
        setProfilePhotoUrl(blobUrl);
      }
    }
  };

  useEffect(() => {
    return () => {
      if (profilePhotourl && profilePhotourl.startsWith('blob:')) {
        URL.revokeObjectURL(profilePhotourl);
      }
    };
  }, [profilePhotourl]);

  // Render the top heading
  const getModalTitle = () => {
    if (action === Actions.addStaff) {
      return trans('ADDSTAFF');
    } else if (action === Actions.editStaff) {
      return trans('EDITSTAFF');
    }
    return '';
  };
  // Render profile picture
  const renderProfileAvatar = () => {
    if (!isEditForm) {
      return (
        <div className="emptyprofile">
          <ContactImage className="user-profile-pic" style={{ width: '200px', height: '200px' }} />
        </div>
      );
    }

    return (
      <Upload
        onChange={handleUploadChange}
        showUploadList={false}
        customRequest={({ file, onSuccess }) => {
          setTimeout(() => {
            onSuccess('ok');
          }, 0);
        }}
        accept=".png,.jpg,.jpeg,.gif,.jfif,.webp,.svg,.tiff"
      >
        <div className="profile-avatar-container">
          {profilePhotourl ? (
            <img
              className="profile-avatar profile-avatar-border"
              src={profilePhotourl}
              alt="Avatar"
            />
          ) : (
            <ContactImage
              className="user-profile-pic"
              style={{ width: '200px', height: '200px' }}
            />
          )}
          <div className="staff-form-profile-edit-container">
            <div className="upload-remove-picture upload">
              <span className="label upload">
                <EditIcon className="edit-delete-icon" /> {trans('UPLOADPICTURE')}
              </span>
            </div>
          </div>
        </div>
      </Upload>
    );
  };
  // If it's a modal, render without MainPage wrapper
  const formContent = (
    <div className="staff-form-container page-content-wrapper">
      <Spin spinning={processing || fetchingUserInfo} wrapperClassName="staff-form custom-form">
        <CustomDialogBox
          isModalOpen={isDialogModalOpen}
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          showDeleteDescrPath={true}
          modalHeader={trans('DELETESTAFF')}
          userFullName={record?.firstname}
          className=""
        />
        <div className="form-group-1">
          <div className="staff-form-title">
            {getModalTitle()}
            <div>
              <CloseIcon
                style={{
                  height: '20px',
                  width: '20px',
                  cursor: 'pointer',
                  fill: '#4E758E',
                }}
                onClick={() => handleModalClose()}
              />
            </div>
          </div>
          <div className="staff-form-data-conatiner">
            <div className="staff-form-profile-photo-container">{renderProfileAvatar()}</div>
            <div className="staff-form-data">
              <div className="staff-name">{trans('WHOISTHENEWSTAFF?')}</div>
              <div className="conatiner-1">
                <div className="form-fields-row">
                  <div className="staff-form-fields">
                    {renderFormItem({
                      name: 'firstname',
                      label: trans('FIRSTNAME*'),
                      placeholder: trans('ENTERFIRSTNAME'),
                      tabIndex: 1,
                    })}
                  </div>
                  <div className="staff-form-fields">
                    {renderFormItem({
                      name: 'lastname',
                      label: trans('LASTNAME*'),
                      placeholder: trans('ENTERLASTNAME'),
                      tabIndex: 2,
                    })}
                  </div>
                </div>
                {nameFieldError && <div className="form-name-field-error">{nameFieldError}</div>}
                <div className="form-fields-row">
                  <div className="staff-form-fields">
                    {renderFormItem({
                      name: 'staffrole',
                      label: trans('STAFFFORMJOBDESCRIPTION'),
                      placeholder: trans('ENTER_JOBDESCRIPTION_L'),
                      tabIndex: 3,
                    })}
                  </div>
                  <div className="staff-form-fields">
                    {renderFormItem({
                      name: 'email',
                      label: trans('EMAIL*'),
                      placeholder: trans('ENTER_EMAIL_L'),
                      disabled: isEditForm,
                      tabIndex: 4,
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Divider className="divider" />
        <div
          className="form-group-2"
          style={{ padding: '10px', display: 'flex', flexDirection: 'column' }}
        >
          <div
            className="form-card"
            style={{
              boxShadow: 'none',
              padding: '0px',
              marginBottom: '0px',
              marginTop: '0px',
              maxWidth: 'none',
            }}
          >
            {renderRole({
              label: `${trans('WHATROLEDOESTHISSTAFFMEMBERHAVEWITH')} ${currentUser?.name}?*`,
              className: 'top-margin staff-name',
            })}
          </div>
          <div className="manadatory-text">{trans('*THESEFIELDSAREMANDATORY')}</div>
        </div>
        <div className="staff-button-container">
          {allowDeletePermission && isEditForm && !isSameUser && (
            <div className="form-button">
              <NewCustomButton
                className={'delete-form'}
                label={trans('DELETESTAFF')}
                icon={<DeleteIcon className="delete-icon" />}
                onClick={handleDialogClick}
              />
            </div>
          )}
          <div className="form-button">
            <NewCustomButton
              className="cancel-form-icon"
              onClick={() => {
                (setFormData(() => userInfo ?? DefaultValues), onActionClick(null, null)());
              }}
              label={trans('CANCEL')}
              icon={<NewCloseIcon className="cancel-icon" />}
            />
          </div>
          <div className="form-button">
            <NewCustomButton
              type={ButtonTypes?.primary}
              label={isEditForm ? trans('EDITSTAFF') : trans('ADDSTAFF')}
              icon={
                <NewUpdateIcon
                  className={
                    (isEditForm ? !areButtonsDisabled : isValid)
                      ? 'update-icon-enabled'
                      : 'update-icon-disabled'
                  }
                />
              }
              onClick={handleSubmit}
              disabled={isEditForm ? areButtonsDisabled : !isValid}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
  return (
    <>
      {showModal && <RebootResetModal data={errorData} />}
      {isModal ? formContent : <MainPage hasSider={true}>{formContent}</MainPage>}
    </>
  );
};

StaffForm.propTypes = {
  parentBread: PropTypes.arrayOf(PropTypes.object),
  record: PropTypes.object,
  action: PropTypes.string,
  onActionClick: PropTypes.func,
  handleModalClose: PropTypes.func,
  staffList: PropTypes.arrayOf(PropTypes.object),
  isModal: PropTypes.bool,
};

export default StaffForm;
